import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';

// Tariffe Section Component
const TariffeSection = ({ tariffs, systemId, canManage, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const emptyForm = {
    tarifname: '',
    preisstufen: [{ zeitraum: '', preis: '' }],
    provision_vorvermarktung: '',
    provision_bauvermarktung: '',
    provision_nachvermarktung: ''
  };
  const [form, setForm] = useState(emptyForm);

  const hasVor  = tariffs.some(t => t.provision_vorvermarktung);
  const hasBau  = tariffs.some(t => t.provision_bauvermarktung);
  const hasNach = tariffs.some(t => t.provision_nachvermarktung);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      tarifname: t.tarifname || '',
      preisstufen: t.preisstufen?.length ? t.preisstufen.map(p => ({ zeitraum: p.zeitraum || '', preis: p.preis || '' })) : [{ zeitraum: '', preis: '' }],
      provision_vorvermarktung: t.provision_vorvermarktung || '',
      provision_bauvermarktung: t.provision_bauvermarktung || '',
      provision_nachvermarktung: t.provision_nachvermarktung || '',
    });
    setShowModal(true);
  };

  const updateStufe = (i, field, val) => {
    const updated = form.preisstufen.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    setForm({ ...form, preisstufen: updated });
  };
  const addStufe    = () => setForm({ ...form, preisstufen: [...form.preisstufen, { zeitraum: '', preis: '' }] });
  const removeStufe = (i) => setForm({ ...form, preisstufen: form.preisstufen.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    if (!form.tarifname.trim()) return;
    const cleaned = form.preisstufen.filter(s => s.zeitraum.trim() || s.preis.trim());
    const payload = {
      tarifname: form.tarifname,
      preisstufen: cleaned,
      provision_vorvermarktung: form.provision_vorvermarktung || null,
      provision_bauvermarktung: form.provision_bauvermarktung || null,
      provision_nachvermarktung: form.provision_nachvermarktung || null,
      order: editing?.order || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API}/process-systems/${systemId}/tariffs/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/process-systems/${systemId}/tariffs`, payload);
      }
      setShowModal(false);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/process-systems/${systemId}/tariffs/${id}`);
    setConfirmDeleteId(null);
    onRefresh();
  };

  // Compact display of price tiers: "24,99€ → 39,99€"
  const fmtPreis = (stufen) => {
    if (!stufen?.length) return '–';
    const filled = stufen.filter(s => s.preis);
    if (!filled.length) return '–';
    return filled.map(s => `${s.preis} €`).join(' → ');
  };
  const fmtProv = (val) => val ? `${val} €` : '–';

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Tarife & Provisionen</h2>
          <p className="text-sm text-gray-500 mt-1">Übersicht aller Tarife mit Preisstruktur und Provision</p>
        </div>
        {canManage && (
          <button onClick={openAdd} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
            + Tarif hinzufügen
          </button>
        )}
      </div>

      {tariffs.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <div className="text-4xl mb-3">💶</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Noch keine Tarife eingetragen</h3>
          <p className="text-sm text-gray-500">{canManage ? 'Füge den ersten Tarif hinzu.' : 'Tarife werden bald verfügbar sein.'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Tarifname</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Tarifkosten</th>
                {hasVor  && <th className="text-left px-4 py-3 font-semibold text-amber-700">Prov. Vorvermarktung</th>}
                {hasBau  && <th className="text-left px-4 py-3 font-semibold text-amber-700">Prov. Bauvermarktung</th>}
                {hasNach && <th className="text-left px-4 py-3 font-semibold text-amber-700">Prov. Nachvermarktung</th>}
                {canManage && <th className="px-4 py-3"/>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tariffs.map((t, i) => (
                <tr key={t.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="px-4 py-3 font-medium text-gray-900">{t.tarifname}</td>
                  <td className="px-4 py-3">
                    {t.preisstufen?.length > 0 && t.preisstufen.some(s => s.preis) ? (
                      <div className="space-y-0.5">
                        {t.preisstufen.filter(s => s.preis).map((s, si) => (
                          <div key={si} className="flex items-center gap-2 text-xs">
                            {s.zeitraum && <span className="text-gray-400">{s.zeitraum}:</span>}
                            <span className="font-semibold text-gray-800">{s.preis} €</span>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-gray-400">–</span>}
                  </td>
                  {hasVor  && <td className="px-4 py-3 font-medium text-green-700">{fmtProv(t.provision_vorvermarktung)}</td>}
                  {hasBau  && <td className="px-4 py-3 font-medium text-green-700">{fmtProv(t.provision_bauvermarktung)}</td>}
                  {hasNach && <td className="px-4 py-3 font-medium text-green-700">{fmtProv(t.provision_nachvermarktung)}</td>}
                  {canManage && (
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEdit(t)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">Bearbeiten</button>
                        {confirmDeleteId === t.id ? (
                          <div className="flex gap-1">
                            <button onClick={() => handleDelete(t.id)} className="text-xs px-2 py-1 rounded bg-red-600 text-white">Ja, löschen</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700">Abbrechen</button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmDeleteId(t.id)} className="text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700">Löschen</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-5">{editing ? 'Tarif bearbeiten' : 'Neuer Tarif'}</h3>
            <div className="space-y-5">

              {/* Tarifname */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarifname *</label>
                <input
                  value={form.tarifname}
                  onChange={e => setForm({ ...form, tarifname: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="z.B. Red S"
                />
              </div>

              {/* Preisstruktur */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Preisstruktur</label>
                  <button onClick={addStufe} className="text-xs text-amber-600 hover:text-amber-700 font-medium">+ Phase hinzufügen</button>
                </div>
                <div className="space-y-2">
                  {form.preisstufen.map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={s.zeitraum}
                        onChange={e => updateStufe(i, 'zeitraum', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                        placeholder="Zeitraum (z.B. Monate 1–6)"
                      />
                      <input
                        value={s.preis}
                        onChange={e => updateStufe(i, 'preis', e.target.value)}
                        className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                        placeholder="Preis (€)"
                      />
                      {form.preisstufen.length > 1 && (
                        <button onClick={() => removeStufe(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Zeitraum ist optional — Preis allein reicht</p>
              </div>

              {/* Provisionen */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Provision (leer lassen wenn nicht vorhanden)</p>
                <div className="space-y-3">
                  {[
                    ['provision_vorvermarktung',  'Vorvermarktung (€)'],
                    ['provision_bauvermarktung',  'Bauvermarktung (€)'],
                    ['provision_nachvermarktung', 'Nachvermarktung (€)'],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <input
                        value={form[key]}
                        onChange={e => setForm({ ...form, [key]: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                        placeholder="z.B. 45"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleSave} disabled={!form.tarifname.trim()} className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {editing ? 'Speichern' : 'Hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Process Systems View Component
const ProcessSystemsView = ({ 
  user, 
  selectedSystem, 
  setSelectedSystem,
  // Modal states from parent
  showSystemModal,
  setShowSystemModal,
  showGuideModal,
  setShowGuideModal,
  showFaqModal,
  setShowFaqModal,
  showDocumentModal,
  setShowDocumentModal,
  // Form states from parent
  systemForm,
  setSystemForm,
  guideForm,
  setGuideForm,
  faqForm,
  setFaqForm,
  // Editing states from parent
  editingSystem,
  setEditingSystem,
  editingGuide,
  setEditingGuide,
  editingFaq,
  setEditingFaq,
  // Upload states from parent
  uploadFile,
  setUploadFile,
  uploadDescription,
  setUploadDescription,
  uploading,
  setUploading,
  // Handler functions from parent
  handleCreateSystem,
  handleUpdateSystem,
  handleCreateGuide,
  handleUpdateGuide,
  handleCreateFaq,
  handleUpdateFaq,
  handleUploadDocument,
  handleDeleteDocument,
  handleDeleteSystem,
  // Refresh function to update lists
  refreshSystemDetails,
  // Notification function from parent
  showSuccessNotification
}) => {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guides, setGuides] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [tariffForm, setTariffForm] = useState({
    tarifname: '', tarifkosten: '',
    provision_vorvermarktung: '', provision_bauvermarktung: '', provision_nachvermarktung: ''
  });
  const [confirmDeleteTariffId, setConfirmDeleteTariffId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // canManage: admin and backoffice can create/edit/delete systems
  const canManage = user?.role === 'admin' || user?.role === 'backoffice';

  // Tab state for system detail view
  const [activeTab, setActiveTab] = useState('guides');

  useEffect(() => {
    fetchSystems();
  }, []);

  useEffect(() => {
    if (selectedSystem) {
      fetchSystemDetails(selectedSystem.id);
    }
  }, [selectedSystem]);

  const fetchSystems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/process-systems`);
      setSystems(response.data);
    } catch (error) {
      console.error('Error fetching process systems:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemDetails = async (systemId) => {
    try {
      const [guidesRes, faqsRes, docsRes, tariffsRes] = await Promise.all([
        axios.get(`${API}/process-systems/${systemId}/guides`),
        axios.get(`${API}/process-systems/${systemId}/faqs`),
        axios.get(`${API}/process-systems/${systemId}/documents`),
        axios.get(`${API}/process-systems/${systemId}/tariffs`),
      ]);
      setGuides(guidesRes.data);
      setFaqs(faqsRes.data);
      setDocuments(docsRes.data);
      setTariffs(tariffsRes.data || []);
    } catch (error) {
      console.error('Error fetching system details:', error);
    }
  };

  // Create a ref to store the refresh function
  const refreshRef = React.useRef();
  
  // Set the refresh function to our local fetchSystemDetails and fetchSystems
  React.useEffect(() => {
    refreshRef.current = () => {
      fetchSystems(); // Refresh the systems list
      if (selectedSystem) {
        fetchSystemDetails(selectedSystem.id);
      }
    };
  }, [selectedSystem]);

  // Pass the refresh function back to parent via callback
  React.useEffect(() => {
    if (refreshSystemDetails && refreshRef.current) {
      refreshSystemDetails(refreshRef.current);
    }
  }, [refreshSystemDetails]);

  const handleDeleteSystemInternal = async (systemId) => {
    try {
      await axios.delete(`${API}/process-systems/${systemId}`);
      if (selectedSystem?.id === systemId) setSelectedSystem(null);
      setConfirmDeleteId(null);
      await fetchSystems();
      if (showSuccessNotification) showSuccessNotification('System erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting system:', error);
    }
  };

  const handleUpdateSystemInternal = async () => {
    try {
      await axios.put(`${API}/process-systems/${editingSystem.id}`, systemForm);
      setShowSystemModal(false);
      setEditingSystem(null);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      await fetchSystems();
      toast.success('System erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating system:', error);
      toast.error('Fehler beim Aktualisieren des Systems');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (selectedSystem) {
    return (
      <div>
        {/* Back Button */}
        <button
          onClick={() => setSelectedSystem(null)}
          className="mb-6 flex items-center space-x-2 text-blue-600 hover:text-blue-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>Zurück zur Übersicht</span>
        </button>

        {/* System Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* System Header with Logo */}
              <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden" 
                   style={{ backgroundColor: selectedSystem.logo_url ? 'transparent' : '#2563eb' }}>
                {selectedSystem.logo_url ? (
                  <img 
                    src={`${API}/system-logos/${selectedSystem.logo_url.split('/').pop()}`}
                    alt={`${selectedSystem.name} Logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.error('Logo loading failed:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.parentElement.style.backgroundColor = '#2563eb';
                      e.target.parentElement.innerHTML = `
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      `;
                    }}
                  />
                ) : (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{selectedSystem.name}</h1>
                <p className="text-gray-600">{selectedSystem.description}</p>
                {selectedSystem.system_url && (
                  <a 
                    href={selectedSystem.system_url.startsWith('http') ? selectedSystem.system_url : `https://${selectedSystem.system_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 mt-2"
                    data-testid="system-open-url"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>System öffnen</span>
                  </a>
                )}
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                  selectedSystem.type === 'client' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {selectedSystem.type === 'client' ? 'Auftraggeber' : 'Internes System'}
                </span>
              </div>
            </div>
            {canManage && (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingSystem(selectedSystem);
                    setSystemForm({
                      name: selectedSystem.name,
                      description: selectedSystem.description || '',
                      type: selectedSystem.type,
                      system_url: selectedSystem.system_url || '',
                      order: selectedSystem.order || 0,
                      logo_url: selectedSystem.logo_url || ''
                    });
                    setShowSystemModal(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Bearbeiten
                </button>
                {confirmDeleteId === selectedSystem.id ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                    <span className="text-sm text-red-700 font-medium">Wirklich löschen?</span>
                    <button
                      onClick={() => handleDeleteSystemInternal(selectedSystem.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors"
                    >
                      Ja, löschen
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(selectedSystem.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Löschen
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content Tabs */}
        <div className="bg-white rounded-xl shadow-lg">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button 
                onClick={() => setActiveTab('guides')}
                className={`px-6 py-4 border-b-2 font-medium ${
                  activeTab === 'guides'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📋 Anleitungen
              </button>
              <button 
                onClick={() => setActiveTab('faqs')}
                className={`px-6 py-4 border-b-2 font-medium ${
                  activeTab === 'faqs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                ❓ FAQ
              </button>
              <button 
                onClick={() => setActiveTab('documents')}
                className={`px-6 py-4 border-b-2 font-medium ${
                  activeTab === 'documents'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📁 Dokumente
              </button>
              {selectedSystem?.type === 'client' && (
                <button
                  onClick={() => setActiveTab('tariffs')}
                  className={`px-6 py-4 border-b-2 font-medium ${
                    activeTab === 'tariffs'
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  💶 Tarife
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {/* Guides Section with Step-by-Step View */}
            {activeTab === 'guides' && (
              <GuidesSection 
                guides={guides} 
                user={user}
                onEditGuide={(guide) => {
                  setEditingGuide(guide);
                  setGuideForm({
                    title: guide.title,
                    description: guide.description || '',
                    steps: guide.steps || [],
                    order: guide.order || 0
                  });
                  setShowGuideModal(true);
                }}
                onAddGuide={() => {
                  setEditingGuide(null);
                  setGuideForm({ 
                    title: '', 
                    description: '',
                    steps: [],
                    order: 0 
                  });
                  setShowGuideModal(true);
                }}
              />
            )}

            {/* FAQ Section with Accordion */}
            {activeTab === 'faqs' && (
              <FAQSection 
                faqs={faqs} 
                user={user}
                onEditFaq={(faq) => {
                  setEditingFaq(faq);
                  setFaqForm({
                    question: faq.question,
                    answer: faq.answer,
                    category: faq.category || '',
                    order: faq.order || 0
                  });
                  setShowFaqModal(true);
                }}
                onAddFaq={() => {
                  setEditingFaq(null);
                  setFaqForm({ question: '', answer: '', category: '', order: 0 });
                  setShowFaqModal(true);
                }}
              />
            )}

            {/* Documents Section */}
            {activeTab === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Dokumente & Screenshots</h2>
                {canManage && (
                  <button 
                    onClick={() => setShowDocumentModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    + Dokument hochladen
                  </button>
                )}
              </div>
              
              {documents.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                  <div className="text-4xl mb-4">📁</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Dokumente</h3>
                  <p className="text-gray-600">
                    {canManage 
                      ? 'Laden Sie die ersten Dokumente für dieses System hoch.'
                      : 'Dokumente werden bald verfügbar sein.'
                    }
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`w-10 h-10 rounded flex items-center justify-center ${
                          doc.file_type === 'pdf' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          <span className={`text-xs font-bold ${
                            doc.file_type === 'pdf' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {doc.file_type.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{doc.filename}</h4>
                          <p className="text-sm text-gray-500">{Math.round(doc.file_size / 1024)} KB</p>
                        </div>
                      </div>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                      )}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/process-documents/${doc.id}/download`;
                            link.download = doc.filename;
                            link.click();
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Herunterladen
                        </button>
                        {canManage && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Löschen
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Tariffs Section */}
            {activeTab === 'tariffs' && (
              <TariffeSection
                tariffs={tariffs}
                systemId={selectedSystem?.id}
                canManage={canManage}
                onRefresh={() => fetchSystemDetails(selectedSystem.id)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }


  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">unfold's Wikipedia</h1>
            <p className="text-gray-600">
              Nachschlagewerk für Auftraggeber und Interne Systeme
            </p>
          </div>
          {canManage && (
            <button
              onClick={() => {
                console.log('📋 System hinzufügen Button clicked!');
                setEditingSystem(null);
                setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
                setShowSystemModal(true);
                console.log('📋 showSystemModal set to:', true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>System hinzufügen</span>
            </button>
          )}
        </div>
      </div>

      {/* Systems Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Client Systems */}
        <div className="md:col-span-2 lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">👥 Auftraggeber</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {systems.filter(s => s.type === 'client').map((system) => (
              <div
                key={system.id}
                onClick={() => setSelectedSystem(system)}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
                       style={{ backgroundColor: system.logo_url ? 'transparent' : '#2563eb' }}>
                    {system.logo_url ? (
                      <img 
                        src={`${API}/system-logos/${system.logo_url.split('/').pop()}`}
                        alt={`${system.name} Logo`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Logo loading failed:', e.target.src);
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = '#2563eb';
                          e.target.parentElement.innerHTML = `
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          `;
                        }}
                      />
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  {canManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSystem(system);
                        setSystemForm({
                          name: system.name,
                          description: system.description || '',
                          type: system.type,
                          system_url: system.system_url || '',
                          order: system.order || 0,
                          logo_url: system.logo_url || ''
                        });
                        setShowSystemModal(true);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{system.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{system.description}</p>
                <div className="flex items-center text-blue-600 text-sm font-medium">
                  <span>Anleitung anzeigen</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Internal Systems */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🔧 Interne Systeme</h2>
          <div className="space-y-4">
            {systems.filter(s => s.type === 'internal').map((system) => (
              <div
                key={system.id}
                onClick={() => setSelectedSystem(system)}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
                       style={{ backgroundColor: system.logo_url ? 'transparent' : '#16a34a' }}>
                    {system.logo_url ? (
                      <img 
                        src={`${API}/system-logos/${system.logo_url.split('/').pop()}`}
                        alt={`${system.name} Logo`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Logo loading failed:', e.target.src);
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = '#16a34a';
                          e.target.parentElement.innerHTML = `
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          `;
                        }}
                      />
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </div>
                  {canManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSystem(system);
                        setSystemForm({
                          name: system.name,
                          description: system.description || '',
                          type: system.type,
                          system_url: system.system_url || '',
                          order: system.order || 0,
                          logo_url: system.logo_url || ''
                        });
                        setShowSystemModal(true);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{system.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{system.description}</p>
                <div className="flex items-center text-green-600 text-sm font-medium">
                  <span>System öffnen</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {systems.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-6">🔧</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Noch keine Systeme</h2>
          <p className="text-gray-600 mb-6">
            {canManage 
              ? 'Beginnen Sie mit der Erstellung Ihres ersten Prozess-Systems.'
              : 'Prozess-Systeme werden bald verfügbar sein.'
            }
          </p>
          {canManage && (
            <button
              onClick={() => {
                setEditingSystem(null);
                setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
                setShowSystemModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Erstes System erstellen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// FAQ Accordion Component
const FAQSection = ({ faqs, user, onEditFaq, onAddFaq }) => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (faqId) => {
    setOpenFaqId(openFaqId === faqId ? null : faqId);
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Häufig gestellte Fragen</h2>
        {user.role === 'admin' && (
          <button
            onClick={onAddFaq}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            + FAQ hinzufügen
          </button>
        )}
      </div>
      
      {faqs.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">❓</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine FAQs</h3>
          <p className="text-gray-600">
            {user.role === 'admin' 
              ? 'Fügen Sie die ersten FAQs für dieses System hinzu.'
              : 'FAQs werden bald verfügbar sein.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div 
                onClick={() => toggleFaq(faq.id)}
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex justify-between items-center"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                    {faq.category && (
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {faq.category}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {user.role === 'admin' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditFaq(faq);
                      }}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="FAQ bearbeiten"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  <div className={`transition-transform duration-200 ${openFaqId === faq.id ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className={`transition-all duration-300 ease-in-out ${
                openFaqId === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="pt-4">
                    <div className="prose max-w-none text-gray-700">
                      {faq.answer.split('\n').map((line, index) => (
                        <p key={index} className="mb-2 last:mb-0">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Structured Guides Component  
const GuidesSection = ({ guides, user, onEditGuide, onAddGuide }) => {
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Schritt-für-Schritt Anleitungen</h2>
        {user.role === 'admin' && (
          <button
            onClick={onAddGuide}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            + Anleitung hinzufügen
          </button>
        )}
      </div>
      
      {guides.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Anleitungen</h3>
          <p className="text-gray-600">
            {user.role === 'admin' 
              ? 'Fügen Sie die erste Anleitung für dieses System hinzu.'
              : 'Anleitungen werden bald verfügbar sein.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {guides.map((guide) => (
            <div key={guide.id} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{guide.title}</h3>
                  {guide.description && (
                    <p className="text-gray-600 mb-4">{guide.description}</p>
                  )}
                </div>
                {user.role === 'admin' && (
                  <button
                    onClick={() => onEditGuide(guide)}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Bearbeiten</span>
                  </button>
                )}
              </div>

              {/* Display structured steps if available, otherwise show legacy content */}
              {guide.steps && guide.steps.length > 0 ? (
                <div className="space-y-4">
                  {guide.steps.map((step, index) => (
                    <div key={step.id || index} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded-r-lg">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {step.step_number || index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                          <div className="prose max-w-none text-gray-700 mb-3">
                            {step.description.split('\n').map((line, lineIndex) => (
                              <p key={lineIndex} className="mb-2 last:mb-0">{line}</p>
                            ))}
                          </div>
                          <div className="mt-3 space-x-2">
                            {step.screenshot_file_path && (
                              <div className="mb-3">
                                <img 
                                  src={`${API}/screenshots/${step.screenshot_file_path.split('/').pop()}`}
                                  alt={`Screenshot für ${step.title}`}
                                  className="max-w-md h-auto rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                  onError={(e) => {
                                    console.error('Screenshot loading failed:', e.target.src);
                                    e.target.style.display = 'none';
                                  }}
                                  onClick={() => {
                                    // Open lightbox
                                    const lightbox = document.createElement('div');
                                    lightbox.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4';
                                    lightbox.onclick = (e) => {
                                      if (e.target === lightbox) {
                                        document.body.removeChild(lightbox);
                                      }
                                    };
                                    
                                    const img = document.createElement('img');
                                    img.src = `${API}/screenshots/${step.screenshot_file_path.split('/').pop()}`;
                                    img.className = 'max-w-full max-h-full object-contain';
                                    img.alt = `Screenshot für ${step.title}`;
                                    
                                    const closeBtn = document.createElement('button');
                                    closeBtn.innerHTML = '×';
                                    closeBtn.className = 'absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300';
                                    closeBtn.onclick = () => document.body.removeChild(lightbox);
                                    
                                    lightbox.appendChild(img);
                                    lightbox.appendChild(closeBtn);
                                    document.body.appendChild(lightbox);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Legacy content display for backward compatibility
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-yellow-800 font-medium">Alte Anleitung (wird migriert)</span>
                  </div>
                  <div 
                    className="prose max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: guide.content || '' }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProcessSystemsView;
